import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { distinctUntilChanged, filter, first, map, pairwise, tap } from 'rxjs/operators';

import { AppVersionService } from '@app/services/app-version.service';
import { OidcStorageService } from '@app/services/oidc-storage.service';
import { selectedDashboardMode } from '@dashboards/store/actions/dashboard.actions';
import { NotificationsService } from '@main-application/notifications/services/notifications.service';
import { loadEnumerations } from '@shared/actions/enumerations.actions';
import { setPreviousRoute } from '@shared/actions/route-state.action';
import { loadTenantData, loadUserData, setAuthenticated } from '@shared/actions/user.actions';
import { setWindowWidth } from '@shared/actions/window-resize.actions';
import { getDashboardMode } from '@shared/functions/get-dashboard-mode.function';
import { TenantData } from '@shared/interfaces/tenant-data';
import { UserData } from '@shared/interfaces/user-data';
import { selectTenantData, selectUserData } from '@shared/selectors/user.selectors';
import { AuthService } from '@shared/services/auth.service';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { environment } from 'src/environments/environment';

import { closeMenus } from './boards/store/boards.actions';

@UntilDestroy()
@Component({
  selector: 'app-main-application',
  templateUrl: './main-application.component.html',
  styleUrls: ['./main-application.component.scss'],
})
export class MainApplicationComponent implements OnInit, OnDestroy {
  environment = environment;
  currentVersion = '';
  currentVersionTimeStamp = 0;

  constructor(
    protected cdr: ChangeDetectorRef,
    private authService: AuthService,
    private router: Router,
    private oidcSecurityService: OidcSecurityService,
    private store: Store<{}>,
    private notificationsService: NotificationsService,
    private snackbarService: SnackbarService,
    private appVersionService: AppVersionService,
    private activatedRoute: ActivatedRoute,
    private oidcStorageService: OidcStorageService
  ) {}

  ngOnInit() {
    this.tokenRevoke();

    this.store.dispatch(setWindowWidth({ windowWidth: window.innerWidth }));
    this.authService.watchForAccessToken().pipe(untilDestroyed(this)).subscribe();

    this.appVersionService.version$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        filter(appVersion => appVersion.version_timestamp > 0),
        first(),
        tap(appVersion => {
          this.currentVersionTimeStamp = appVersion.version_timestamp;
          this.currentVersion = appVersion.version_number;
        })
      )
      .subscribe();

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.checkSlackResponse();
        this.checkBuildiumResponse();
        this.cdr.detectChanges();
      });

    this.oidcSecurityService.isAuthenticated$
      .pipe(
        untilDestroyed(this),
        map(({ isAuthenticated }) => isAuthenticated),
        distinctUntilChanged()
      )
      .subscribe(isAuthenticated => {
        this.store.dispatch(setAuthenticated({ isAuthenticated }));

        if (!isAuthenticated) {
          this.notificationsService.close();
          return;
        }

        this.store.dispatch(loadTenantData());
        this.store.dispatch(loadUserData());
        this.store.dispatch(loadEnumerations());
        this.notificationsService.connect();
      });

    this.store
      .select(selectUserData)
      .pipe(
        untilDestroyed(this),
        filter((userData: UserData) => !!userData)
      )
      .subscribe((userData: UserData) =>
        this.store.dispatch(selectedDashboardMode({ dashboardMode: getDashboardMode(userData) }))
      );

    this.store
      .select(selectTenantData)
      .pipe(
        untilDestroyed(this),
        filter((tenantData: TenantData) => !!tenantData?.tenantId)
      )
      .subscribe((tenantData: TenantData) => {
        const { href, hostname } = window.location;
        if (hostname !== 'localhost' && !href.includes(tenantData.tenantUrl)) {
          setTimeout(() => {
            const url = new URL(tenantData.tenantUrl);
            url.searchParams.append('revoke-token', '1');
            window.location.href = url.toString();
          }, 0);
        }
      });

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(evt => evt instanceof RoutesRecognized),
        map(evt => evt as RoutesRecognized),
        pairwise()
      )
      .subscribe(([previousEvent, _event]) => {
        if (previousEvent?.state) {
          this.store.dispatch(setPreviousRoute({ previousRoute: previousEvent.urlAfterRedirects }));
        }
      });

    this.appVersionService.pull();
  }

  checkSlackResponse() {
    const { slackAdded } = this.activatedRoute.snapshot.queryParams;
    if (slackAdded === 'true') {
      this.removeQueryParam('slackAdded');
      setTimeout(() => {
        this.snackbarService.success(SnackbarErrorMessage.SlackAdded);
      }, 1000);
    }
  }

  checkBuildiumResponse() {
    const { buildium } = this.activatedRoute.snapshot.queryParams;
    if (buildium === 'true') {
      this.removeQueryParam('buildium');
      setTimeout(() => {
        this.snackbarService.success(SnackbarErrorMessage.BuildiumSetupCompleted);
      }, 1000);
    }
    if (buildium === 'false') {
      this.removeQueryParam('buildium');
      setTimeout(() => {
        this.snackbarService.error(SnackbarErrorMessage.BuildiumSetupIncompleted);
      }, 1000);
    }
  }

  private removeQueryParam(paramName: string) {
    this.router.navigate([], {
      queryParams: {
        [paramName]: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy() {
    this.notificationsService.close();
  }

  @HostListener('window:click', ['$event'])
  @HostListener('window:contextmenu', ['$event'])
  onClick(event) {
    this.store.dispatch(closeMenus());
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.store.dispatch(setWindowWidth({ windowWidth: event.target.innerWidth }));
  }

  tokenRevoke() {
    const url = new URL(window.location.href);
    if (url.searchParams.has('revoke-token')) {
      this.oidcStorageService.clear();
      const url = new URL(window.location.href);
      url.searchParams.delete('revoke-token');
      window.location.href = url.toString();
    }
  }
}
